import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { IPhoneCallFilterCriteria, PhoneCallFilterCriteriaProperty } from '../../../../viewModels/phonecall';

// Category value used by API to identify practice calls
const PRACTICE_CALL_CATEGORY = 'PracticeCall';

export interface IPracticeCallFilter {
	date?: Api.IRange<Date>;
	user?: Api.IUser;
}

// Default date range: last 30 days
const getDefaultDateRange = () => {
	const end = new Date();
	const start = new Date();
	start.setDate(end.getDate() - 30);
	return { start, end };
};

// Default filter focusing on practice calls from the last 30 days
// Note: user will be set in the hook using current user session
export const DefaultPracticeCallFilter: IPracticeCallFilter = {
	date: getDefaultDateRange(),
};

interface IPagedCollection<T> {
	pageToken?: string;
	totalCount?: number;
	values: T[];
}

// PhoneCallView response from the API
// Company is returned as a partial company reference with basic fields
export interface IPhoneCallView {
	phoneCall: Api.IPhoneCall;
	company?: { id?: string; companyName?: string };
	contacts?: Api.IContact[];
}

function filterToCriteria(filter: IPracticeCallFilter, currentUserId: string): IPhoneCallFilterCriteria[] {
	const criteria: IPhoneCallFilterCriteria[] = [];

	// Always filter by PracticeCall category
	criteria.push({
		property: PhoneCallFilterCriteriaProperty.Category,
		value: PRACTICE_CALL_CATEGORY,
	});

	// Add date filter if provided (using Gt/Lt operators supported by the API)
	if (filter?.date?.start && filter?.date?.end) {
		criteria.push({
			property: PhoneCallFilterCriteriaProperty.Date,
			value: filter.date.start.toISOString(),
			op: Api.FilterOperator.Gt,
		});
		criteria.push({
			property: PhoneCallFilterCriteriaProperty.Date,
			value: filter.date.end.toISOString(),
			op: Api.FilterOperator.Lt,
		});
	}

	// User filter is required by the API - use filter user or fall back to current user
	const userId = filter?.user?.id || currentUserId;
	criteria.push({
		property: PhoneCallFilterCriteriaProperty.User,
		value: userId,
	});

	// Only show practice calls with transcriptions
	criteria.push({
		property: PhoneCallFilterCriteriaProperty.HasTranscription,
		value: 'true',
	});

	return criteria;
}

export const usePracticeCallsSearch = (initialFilter?: IPracticeCallFilter) => {
	const [filter, setFilter] = React.useState<IPracticeCallFilter>(initialFilter || DefaultPracticeCallFilter);
	const userSession = useUserSession();

	const currentUserId = userSession.user?.id || '';

	const callQuery = useInfiniteQuery({
		queryKey: ['practice-calls-infinite', JSON.stringify(filter), currentUserId],
		queryFn: async ({ pageParam = undefined }): Promise<IPagedCollection<IPhoneCallView>> => {
			const criteria = filterToCriteria(filter, currentUserId);

			return new Promise((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceWithOperationResults<IPagedCollection<IPhoneCallView>>(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: 'phonecall/filter',
						queryParams: {
							pageSize: '25',
							...(pageParam && { pageToken: pageParam }),
						},
					}),
					'POST',
					{ criteria },
					result => {
						if (result.success && result.value?.values) {
							resolve(result.value);
						} else {
							reject(new Error(result.systemMessage || 'Failed to fetch practice calls'));
						}
					},
					error => {
						reject(new Error(error.systemMessage || 'API call failed'));
					}
				);
			});
		},
		getNextPageParam: lastPage => lastPage.pageToken,
		enabled: !!currentUserId, // Only query when we have a user
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
		initialPageParam: undefined,
	});

	// Transform phone call view data - returns the raw view since practice calls have different structure
	const transformToCallAnalysisData = React.useCallback(
		(phoneCallView: IPhoneCallView): IPhoneCallView & { hasTranscription: boolean } => {
			return {
				...phoneCallView,
				hasTranscription: !!phoneCallView.phoneCall?.transcriptionId,
			};
		},
		[]
	);

	const calls = React.useMemo(() => {
		const allCalls: IPhoneCallView[] = [];
		callQuery.data?.pages.forEach(page => {
			allCalls.push(...page.values);
		});
		return allCalls.map(transformToCallAnalysisData);
	}, [callQuery.data, transformToCallAnalysisData]);

	const isLoading = callQuery.isPending || callQuery.isFetchingNextPage;

	const loadMore = React.useCallback(
		(shouldNotTriggerLoading?: boolean) => {
			if (!shouldNotTriggerLoading && callQuery.hasNextPage && !callQuery.isFetchingNextPage) {
				callQuery.fetchNextPage();
			}
		},
		[callQuery]
	);

	const refresh = React.useCallback(async () => {
		callQuery.refetch();
	}, [callQuery]);

	return {
		calls,
		filter,
		setFilter,
		isLoading,
		isEmpty: !callQuery.data?.pages?.[0]?.values?.length,
		loadMore,
		refresh,
		hasNextPage: callQuery.hasNextPage,
		isFetchingNextPage: callQuery.isFetchingNextPage,
		queryState: {
			isError: callQuery.isError,
			error: callQuery.error,
			isSuccess: callQuery.isSuccess,
		},
	};
};
